/**
 * AffiliateSessionContext — the affiliate portal's own session.
 *
 * An affiliate is a separate identity from a WLB member: it signs in against
 * `AffiliateValidateLogin` and holds a bearer that `apiClient` never treats as
 * the member token. The two sessions are independent on purpose, so a member can
 * stay signed in while an affiliate uses the portal, and signing either one out
 * leaves the other alone.
 *
 * There is no refresh endpoint for this bearer, so an expired affiliate token is
 * terminal. `apiClient` announces the rejected token with
 * `AUTH_EVENTS.BEARER_TOKEN_REJECTED`; this provider clears the session on it,
 * which flips `isSignedIn` and lets the route guard return the visitor to
 * sign-in instead of leaving them on a screen that can only ever error.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import affiliateService from '../api/affiliate/affiliateService';
import type { AffiliateAuth } from '../api/affiliate/affiliateApi';
import type {
  AffiliateReviewState,
  AffiliateSession,
} from '../api/affiliate/affiliateTypes';
import {
  AUTH_EVENTS,
  type BearerTokenRejectedPayload,
} from '../api/auth/tokenRefresh';
import { STORAGE_KEYS } from '../constants';
import { eventBus } from '../platform/eventBus';
import { storage } from '../platform/storage';

export interface AffiliateSessionContextValue {
  /** True while the stored session is being read back; gate routing on it. */
  initializing: boolean;
  session: AffiliateSession | null;
  isSignedIn: boolean;
  /** Bearer holder to hand to `affiliateService` calls. */
  auth: AffiliateAuth;
  /** Rejects with a user-facing error; callers surface it in the form. */
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * True when the session was dropped by the API rather than by the affiliate,
   * so sign-in can explain why they are back there. Cleared on the next sign-in.
   */
  expired: boolean;
}

const affiliateSessionContext = createContext<AffiliateSessionContextValue | null>(
  null,
);

const REVIEW_STATES: readonly AffiliateReviewState[] = [
  'approved',
  'rejected',
  'pending',
];

/**
 * Every field is checked, not just the bearer: a partial or older stored shape
 * would otherwise leave `isSignedIn` true while the screens read an undefined
 * `email` / `state`. A session that fails this is treated as absent, which
 * sends the visitor back through sign-in to get a complete one.
 */
export const isStoredAffiliateSession = (
  value: unknown,
): value is AffiliateSession => {
  if (value == null || typeof value !== 'object') return false;
  const candidate = value as Partial<AffiliateSession>;
  return (
    typeof candidate.token === 'string' &&
    candidate.token.length > 0 &&
    typeof candidate.affiliateUserId === 'number' &&
    Number.isFinite(candidate.affiliateUserId) &&
    candidate.affiliateUserId > 0 &&
    typeof candidate.email === 'string' &&
    candidate.email.length > 0 &&
    REVIEW_STATES.includes(candidate.state as AffiliateReviewState)
  );
};

export const AffiliateSessionProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [session, setSession] = useState<AffiliateSession | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [expired, setExpired] = useState(false);
  // Read inside the event listener so it never has to resubscribe per session.
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = session?.token ?? null;

  useEffect(() => {
    let active = true;
    storage
      .getItem<AffiliateSession>(STORAGE_KEYS.AFFILIATE_SESSION)
      .then(stored => {
        if (!active) return;
        if (isStoredAffiliateSession(stored)) {
          setSession(stored);
          return;
        }
        // Drop an unusable record rather than re-reading it on every boot.
        if (stored != null) {
          storage
            .removeItem(STORAGE_KEYS.AFFILIATE_SESSION)
            .catch(() => undefined);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setInitializing(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const next = await affiliateService.login(email, password);
    await storage.setItem(STORAGE_KEYS.AFFILIATE_SESSION, next);
    // The campaign slug only exists to attribute an application; once the
    // affiliate has an account it must not keep overriding their answer.
    await storage.removeItem(STORAGE_KEYS.AFFILIATE_SOURCE);
    setExpired(false);
    setSession(next);
  }, []);

  const signOut = useCallback(async () => {
    setExpired(false);
    setSession(null);
    await storage.removeItem(STORAGE_KEYS.AFFILIATE_SESSION);
  }, []);

  useEffect(() => {
    const subscription = eventBus.addListener<BearerTokenRejectedPayload>(
      AUTH_EVENTS.BEARER_TOKEN_REJECTED,
      payload => {
        // Another identity's bearer may use the same mechanism, so only react
        // when the rejected token is the one this session is holding.
        if (!payload?.bearerToken || payload.bearerToken !== tokenRef.current) {
          return;
        }
        tokenRef.current = null;
        setExpired(true);
        setSession(null);
        storage.removeItem(STORAGE_KEYS.AFFILIATE_SESSION).catch(() => undefined);
      },
    );
    return () => subscription.remove();
  }, []);

  const value = useMemo<AffiliateSessionContextValue>(
    () => ({
      initializing,
      session,
      isSignedIn: session != null,
      auth: session ? { bearerToken: session.token } : {},
      signIn,
      signOut,
      expired,
    }),
    [initializing, session, signIn, signOut, expired],
  );

  return (
    <affiliateSessionContext.Provider value={value}>
      {children}
    </affiliateSessionContext.Provider>
  );
};

export const useAffiliateSession = (): AffiliateSessionContextValue => {
  const value = useContext(affiliateSessionContext);
  if (!value) {
    throw new Error(
      'useAffiliateSession must be used inside <AffiliateSessionProvider>',
    );
  }
  return value;
};

export default affiliateSessionContext;
